import { CueHistory, CueTagSeverity } from 'src/app/models/fincas/cue.model';

/** Presents raw CUE statuses with farmer-friendly wording. */
export class CueStatusPresenter {
  private static readonly PENDING_STATUSES = ['PENDIENTE', 'PENDENT', 'EN_PROCESO', 'PROCESANDO'];

  public static normalize(status?: string | number | null): string {
    return String(status ?? '').trim().toUpperCase();
  }

  public static isPendingStatus(status?: string | number | null): boolean {
    return CueStatusPresenter.PENDING_STATUSES.includes(CueStatusPresenter.normalize(status));
  }

  public static statusHelp(status?: string | number | null): string {
    switch (CueStatusPresenter.normalize(status)) {
      case '0':
      case 'PENDIENTE':
      case 'PENDENT':
      case 'EN_PROCESO':
      case 'PROCESANDO':
        return 'El cuaderno está enviado y pendiente de respuesta.';
      case '1':
      case 'OK':
        return 'La administración ha aceptado todas las actividades del cuaderno.';
      case 'PARCIAL':
        return 'La administración ha aceptado una parte del cuaderno. Revisa las actividades con problemas.';
      case '2':
      case 'FALLO':
        return 'La administración no ha aceptado el cuaderno. Revisa los datos indicados.';
      default:
        return '';
    }
  }

  public static statusSeverity(status?: string | number | null): CueTagSeverity {
    const normalizedStatus = CueStatusPresenter.normalize(status);

    if (normalizedStatus === 'OK' || normalizedStatus === '1') {
      return 'success';
    }

    if (normalizedStatus === 'PARCIAL') {
      return 'warn';
    }

    if (normalizedStatus === 'FALLO' || normalizedStatus === '2') {
      return 'danger';
    }

    if (CueStatusPresenter.isPendingStatus(normalizedStatus) || normalizedStatus === '0') {
      return 'info';
    }

    return 'secondary';
  }

  public static statusLabel(status?: string | number | null): string {
    const normalizedStatus = CueStatusPresenter.normalize(status);

    switch (normalizedStatus) {
      case '1':
      case 'OK':
        return 'Aceptado';
      case 'PARCIAL':
        return 'Aceptado parcialmente';
      case '2':
      case 'FALLO':
        return 'Con problemas';
      case '0':
      case 'PENDIENTE':
      case 'PENDENT':
      case 'EN_PROCESO':
      case 'PROCESANDO':
        return 'Pendiente de respuesta';
      default:
        return normalizedStatus || 'Sin estado';
    }
  }

  public static statusIcon(statusCue: CueHistory | null): string {
    const normalizedStatus = CueStatusPresenter.normalize(statusCue?.estado);

    if (normalizedStatus === 'OK' || normalizedStatus === '1') {
      return 'pi pi-check-circle';
    }

    if (normalizedStatus === 'PARCIAL') {
      return 'pi pi-exclamation-triangle';
    }

    if (normalizedStatus === 'FALLO' || normalizedStatus === '2') {
      return 'pi pi-times-circle';
    }

    if (CueStatusPresenter.isPendingStatus(normalizedStatus) || normalizedStatus === '0') {
      return 'pi pi-clock';
    }

    return 'pi pi-send';
  }

  public static currentStatusText(statusCue: CueHistory | null): string {
    return CueStatusPresenter.statusTitle(statusCue);
  }

  public static statusTitle(statusCue: CueHistory | null): string {
    if (!statusCue) {
      return 'El CUE está preparado para revisar';
    }

    const normalizedStatus = CueStatusPresenter.normalize(statusCue.estado);

    if (CueStatusPresenter.isPendingStatus(normalizedStatus)) {
      return 'El cuaderno está enviado y pendiente de respuesta';
    }

    if (normalizedStatus === 'OK' || normalizedStatus === '1') {
      return 'El cuaderno está aceptado';
    }

    if (normalizedStatus === 'PARCIAL') {
      return 'Hay actividades que necesitan revisión';
    }

    if (normalizedStatus === 'FALLO' || normalizedStatus === '2') {
      return 'El cuaderno tiene problemas';
    }

    return 'Estado pendiente de revisar';
  }

  public static statusDescription(statusCue: CueHistory | null): string {
    if (!statusCue) {
      return 'Comprueba qué se enviará y pulsa el botón de envío cuando los datos estén listos.';
    }

    const normalizedStatus = CueStatusPresenter.normalize(statusCue.estado);

    if (CueStatusPresenter.isPendingStatus(normalizedStatus)) {
      return 'La administración lo está revisando. Puedes dejar esta ventana abierta o volver más tarde.';
    }

    if (normalizedStatus === 'OK' || normalizedStatus === '1') {
      return 'Todas las actividades enviadas han sido aceptadas. No tienes que hacer nada más.';
    }

    if (normalizedStatus === 'PARCIAL') {
      return 'Una parte se ha aceptado. Corrige las actividades con problemas y reintenta solo las pendientes.';
    }

    if (normalizedStatus === 'FALLO' || normalizedStatus === '2') {
      return 'Revisa los avisos, corrige los datos indicados y vuelve a enviar las actividades corregidas.';
    }

    return 'Revisa el historial para entender el estado actual del envío.';
  }


  public static isRetryStatus(status?: string | number | null): boolean {
    const normalizedStatus = CueStatusPresenter.normalize(status);

    return normalizedStatus === 'PARCIAL' || normalizedStatus === 'FALLO' || normalizedStatus === '2';
  }

  public static shouldShowRetryActions(statusCue: CueHistory | null, hasPendingCue: boolean): boolean {
    return Boolean(statusCue) && !hasPendingCue && CueStatusPresenter.isRetryStatus(statusCue?.estado);
  }

  public static sendButtonLabel(statusCue: CueHistory | null): string {
    const normalizedStatus = CueStatusPresenter.normalize(statusCue?.estado);

    if (CueStatusPresenter.isRetryStatus(normalizedStatus)) {
      return 'Reintentar actividades corregidas';
    }

    return 'Enviar cuaderno CUE';
  }
}
